<template>
  <div class="restaurant-halls-page">
    <!-- top bar -->
    <div class="page-header">
      <div class="page-header__title">
        <h1>طراحی و چیدمان سالن</h1>
        <p>ایجاد و ویرایش سالن‌ها و چیدمان میزها</p>
      </div>
      <ab-ui-button
        variant="primary"
        :loading="saving"
        @click="saveLayout"
      >
        <span>ذخیره چیدمان</span>
        <i class="mdi mdi-content-save-outline" aria-hidden="true" />
      </ab-ui-button>
    </div>

    <div class="page-content">
      <!-- right side: sidebars (hall & table settings) -->
      <div class="sidebars">
        <ab-ui-card class="sidebars__card">
          <template #header>
            <div class="sidebars__header">
              <span>مدیریت سالن‌ها</span>
              <ab-ui-button variant="outline" @click="openHallDialog">
                <span>افزودن سالن</span>
                <i class="mdi mdi-plus" aria-hidden="true" />
              </ab-ui-button>
            </div>
          </template>

          <div class="branch-select-wrap">
            <ab-ui-select
              v-model="selectedBranchId"
              label="شعبه"
              placeholder="شعبه را انتخاب کنید"
              :items="branchItems"
            />
          </div>

          <div class="halls-list">
            <div
              v-for="hall in halls"
              :key="hall.id"
              class="hall-item"
              :class="{ 'hall-item--active': hall.id === activeHallId }"
            >
              <button type="button" class="hall-item__main" @click="selectHall(hall.id)">
                <div class="hall-item__title">
                  <span>سالن {{ hall.name }}</span>
                  <small>{{ tablesCountForHall(hall.id) }} میز</small>
                </div>
              </button>
              <div class="hall-item__actions">
                <button type="button" class="hall-item__btn hall-item__btn--edit" title="ویرایش" @click.stop="editHall(hall)">
                  <i class="mdi mdi-pencil-outline" aria-hidden="true" />
                </button>
                <button type="button" class="hall-item__btn hall-item__btn--danger" title="حذف" @click.stop="openDeleteHallConfirm(hall)">
                  <i class="mdi mdi-delete-outline" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </ab-ui-card>

        <ab-ui-card class="sidebars__card">
          <template #header>
            <span>افزودن میز جدید</span>
          </template>
          <div class="add-table-form">
            <ab-ui-input
              v-model="newTable.number"
              label="شماره میز"
              placeholder="مثال: ۱"
            />
            <ab-ui-input
              v-model.number="newTable.capacity"
              type="number"
              label="تعداد صندلی"
              placeholder="نفره ۴"
            />

            <div class="shape-toggle">
              <span class="shape-toggle__label">شکل میز</span>
              <div class="shape-toggle__buttons">
                <button
                  v-for="shape in shapes"
                  :key="shape.value"
                  class="shape-pill"
                  :class="{
                    'shape-pill--active': newTable.shape === shape.value,
                    'primary--text': newTable.shape === shape.value
                  }"
                  @click="newTable.shape = shape.value"
                >
                  <span class="shape-pill__label">{{ shape.label }}</span>
                  <i class="mdi shape-pill__icon" :class="shape.icon" aria-hidden="true" />
                </button>
              </div>
            </div>

            <ab-ui-button
              variant="primary"
              block
              @click="addTable"
            >
              <span>افزودن میز</span>
              <i class="mdi mdi-plus" aria-hidden="true" />
            </ab-ui-button>
          </div>
        </ab-ui-card>
      </div>

      <!-- left side: canvas / board -->
      <ab-ui-card class="canvas-card">
        <div class="canvas-header">
          <span>{{ activeHall ? 'سالن ' + activeHall.name : 'بدون سالن' }}</span>
          <template v-if="selectedTableForActions">
            <div class="canvas-header__table-actions">
              <span class="canvas-header__table-label">میز {{ selectedTableForActions.number }}</span>
              <button type="button" class="canvas-header__action-btn" @click="openTableEdit(selectedTableForActions)">
                <i class="mdi mdi-pencil-outline" aria-hidden="true" />
                ویرایش
              </button>
              <button type="button" class="canvas-header__action-btn canvas-header__action-btn--danger" @click="openDeleteTableConfirm(selectedTableForActions)">
                <i class="mdi mdi-delete-outline" aria-hidden="true" />
                حذف
              </button>
              <button type="button" class="canvas-header__action-btn" @click="selectedTableId = null">
                <i class="mdi mdi-close" aria-hidden="true" />
                لغو
              </button>
            </div>
          </template>
          <small v-else-if="!loadingTables">راهنما: میزها را بکشید و رها کنید</small>
          <small v-else class="text--secondary">در حال بارگذاری میزها...</small>
        </div>
        <div
          class="canvas"
          ref="canvas"
          @mousedown="startDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          @mousemove="onDrag"
        >
          <div
            v-for="table in tablesForActiveHall"
            :key="table.id"
            class="table"
            :class="['table--' + table.shape, 'table--status-' + table.status, { 'table--active': table.id === selectedTableId }]"
            :style="{
              left: table.x + 'px',
              top: table.y + 'px',
            }"
            @mousedown.stop="selectTable(table, $event)"
          >
            <span class="table__number">{{ table.number }}</span>
            <span class="table__seats">{{ table.capacity }} نفر</span>
          </div>
        </div>
        <div class="canvas-footer">
          <span class="legend-item legend-item--free">آزاد</span>
          <span class="legend-item legend-item--busy">پر</span>
          <span class="legend-item legend-item--reserved">رزرو</span>
        </div>
      </ab-ui-card>
    </div>

    <!-- add / edit hall dialog -->
    <transition name="dialog-fade">
      <div v-if="hallDialog" class="dialog-backdrop" @click.self="closeHallDialog">
        <div class="dialog">
        <div class="dialog__header">
          <span>{{ editingHallId ? 'ویرایش سالن' : 'افزودن سالن جدید' }}</span>
          <button type="button" class="dialog__close" @click="closeHallDialog">×</button>
        </div>
        <div class="dialog__body">
          <ab-ui-select
            v-model="newHall.branchId"
            label="شعبه"
            placeholder="شعبه را انتخاب کنید"
            :items="branchItems"
          />
          <ab-ui-input
            v-model="newHall.name"
            label="نام سالن"
            placeholder="مثال: سالن VIP"
          />
          <div class="dialog__row">
            <ab-ui-input
              v-model.number="newHall.width"
              type="number"
              label="عرض (پیکسل)"
              placeholder="800"
            />
            <ab-ui-input
              v-model.number="newHall.height"
              type="number"
              label="ارتفاع (پیکسل)"
              placeholder="600"
            />
          </div>
        </div>
        <div class="dialog__footer">
          <ab-ui-button variant="secondary" @click="closeHallDialog">
            <span>انصراف</span>
            <i class="mdi mdi-close" aria-hidden="true" />
          </ab-ui-button>
          <ab-ui-button variant="primary" @click="submitHall">
            <span>{{ editingHallId ? 'ذخیره' : 'افزودن سالن' }}</span>
            <i class="mdi mdi-plus" aria-hidden="true" />
          </ab-ui-button>
        </div>
      </div>
    </div>
    </transition>

    <!-- delete hall confirmation -->
    <transition name="dialog-fade">
      <div v-if="deleteConfirmHall" class="dialog-backdrop" @click.self="closeDeleteHallConfirm">
      <div class="confirm-dialog">
        <div class="confirm-dialog__icon">
          <i class="mdi mdi-delete-outline" aria-hidden="true" />
        </div>
        <h3 class="confirm-dialog__title">حذف سالن</h3>
        <p class="confirm-dialog__message">
          آیا از حذف سالن «<strong>{{ deleteConfirmHall.name }}</strong>» اطمینان دارید؟
        </p>
        <p class="confirm-dialog__warning">
          <i class="mdi mdi-alert-outline" aria-hidden="true" />
          <span>این عمل قابل بازگشت نیست. میزهای این سالن نیز حذف می‌شوند.</span>
        </p>
        <div class="confirm-dialog__footer">
          <ab-ui-button variant="danger" @click="doDeleteHall">
            <span>حذف</span>
            <i class="mdi mdi-delete-outline" aria-hidden="true" />
          </ab-ui-button>
          <ab-ui-button variant="secondary" @click="closeDeleteHallConfirm">
            <span>انصراف</span>
            <i class="mdi mdi-close" aria-hidden="true" />
          </ab-ui-button>
        </div>
      </div>
    </div>
    </transition>

    <!-- delete table confirmation -->
    <transition name="dialog-fade">
      <div v-if="deleteConfirmTable" class="dialog-backdrop" @click.self="closeDeleteTableConfirm">
      <div class="confirm-dialog">
        <div class="confirm-dialog__icon">
          <i class="mdi mdi-delete-outline" aria-hidden="true" />
        </div>
        <h3 class="confirm-dialog__title">حذف میز</h3>
        <p class="confirm-dialog__message">
          آیا از حذف میز «<strong>{{ deleteConfirmTable.number }}</strong>» اطمینان دارید؟
        </p>
        <p class="confirm-dialog__warning">
          <i class="mdi mdi-alert-outline" aria-hidden="true" />
          <span>این عمل قابل بازگشت نیست.</span>
        </p>
        <div class="confirm-dialog__footer">
          <ab-ui-button variant="danger" @click="doDeleteTable">
            <span>حذف</span>
            <i class="mdi mdi-delete-outline" aria-hidden="true" />
          </ab-ui-button>
          <ab-ui-button variant="secondary" @click="closeDeleteTableConfirm">
            <span>انصراف</span>
            <i class="mdi mdi-close" aria-hidden="true" />
          </ab-ui-button>
        </div>
      </div>
    </div>
    </transition>

    <!-- edit table dialog -->
    <transition name="dialog-fade">
      <div v-if="tableDialog" class="dialog-backdrop" @click.self="closeTableEdit">
      <div class="dialog">
        <div class="dialog__header">
          <span>ویرایش میز</span>
          <button type="button" class="dialog__close" @click="closeTableEdit">×</button>
        </div>
        <div class="dialog__body">
          <ab-ui-input
            v-model="editTableForm.number"
            label="شماره میز"
            placeholder="مثال: ۱"
          />
          <ab-ui-input
            v-model.number="editTableForm.capacity"
            type="number"
            label="تعداد صندلی"
            placeholder="۴"
          />
          <div class="shape-toggle">
            <span class="shape-toggle__label">شکل میز</span>
            <div class="shape-toggle__buttons">
              <button
                v-for="shape in shapes"
                :key="shape.value"
                class="shape-pill"
                :class="{
                  'shape-pill--active': editTableForm.shape === shape.value,
                  'primary--text': editTableForm.shape === shape.value
                }"
                @click="editTableForm.shape = shape.value"
              >
                <span class="shape-pill__label">{{ shape.label }}</span>
                <i class="mdi shape-pill__icon" :class="shape.icon" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div class="dialog__footer">
          <ab-ui-button variant="secondary" @click="closeTableEdit">انصراف</ab-ui-button>
          <ab-ui-button variant="primary" @click="submitTableEdit">ذخیره</ab-ui-button>
        </div>
      </div>
    </div>
    </transition>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      right
      :timeout="3500"
      style="z-index: 9999;"
    >
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import AbUiButton from './partial/AbUiButton.vue'
import AbUiInput from './partial/AbUiInput.vue'
import AbUiCard from './partial/AbUiCard.vue'
import AbUiSelect from './partial/AbUiSelect.vue'
import { setLastBranchId } from './partial/branchStorage.js'
import toastMixin from '@/Mixins/toastMixin'

let tableIdCounter = 1
let hallIdCounter = 1

export default {
  name: 'RestaurantHallsPage',
  mixins: [toastMixin],
  components: {
    AbUiButton,
    AbUiInput,
    AbUiCard,
    AbUiSelect,
  },
  data () {
    return {
      saving: false,
      branches: [],
      selectedBranchId: null,
      loadingBranches: false,
      halls: [
        { id: hallIdCounter++, name: 'سالن اصلی', width: 800, height: 600 },
      ],
      activeHallId: 1,
      tables: [
        {
          id: tableIdCounter++,
          hallId: 1,
          number: '1',
          capacity: 2,
          shape: 'circle',
          status: 'free',
          x: 80,
          y: 80,
        },
        {
          id: tableIdCounter++,
          hallId: 1,
          number: '2',
          capacity: 4,
          shape: 'rectangle',
          status: 'busy',
          x: 200,
          y: 80,
        },
        {
          id: tableIdCounter++,
          hallId: 1,
          number: '3',
          capacity: 6,
          shape: 'rectangle',
          status: 'free',
          x: 340,
          y: 80,
        },
      ],
      newTable: {
        number: '',
        capacity: 4,
        shape: 'rectangle',
      },
      shapes: [
        { value: 'circle', label: 'دایره', icon: 'mdi-circle-outline' },
        { value: 'square', label: 'مربع', icon: 'mdi-square-outline' },
        { value: 'rectangle', label: 'مستطیل', icon: 'mdi-rectangle-outline' },
      ],
      dragging: false,
      dragTableId: null,
      dragOffset: { x: 0, y: 0 },
      hallDialog: false,
      editingHallId: null,
      newHall: {
        branchId: null,
        name: '',
        width: 800,
        height: 600,
      },
      selectedTableId: null,
      tableCountByHallId: {},
      loadingTables: false,
      deleteConfirmHall: null,
      deleteConfirmTable: null,
      tableDialog: false,
      editingTable: null,
      editTableForm: {
        number: '',
        capacity: 4,
        shape: 'rectangle',
      },
    }
  },
  computed: {
    activeHall () {
      return this.halls.find(h => h.id === this.activeHallId) || null
    },
    tablesForActiveHall () {
      return this.tables
    },
    selectedTableForActions () {
      if (!this.selectedTableId) return null
      return this.tables.find(t => t.id === this.selectedTableId) || null
    },
    branchItems () {
      return this.branches.map(b => ({
        value: b.id,
        text: b.name || 'بدون نام',
      }))
    },
  },
  watch: {
    async selectedBranchId (newId) {
      setLastBranchId(newId)
      await this.loadLayoutForBranch(newId)
    },
  },
  async created () {
    await this.fetchBranches()
    if (this.selectedBranchId == null) {
      await this.loadLayoutForBranch(null)
    }
  },
  methods: {
    async fetchBranches () {
      if (!this.$axios || !this.$endpoint) return
      this.loadingBranches = true
      try {
        const res = await this.$axios.get(this.$endpoint + 'branch/list', {
          params: {
            type: 'all',
            options: { itemsPerPage: 1000 },
            page: 1,
          },
        })
        const raw = res.data?.data || []
        this.branches = raw.map(item => ({
          ...item,
          name: item?.name || 'بدون نام',
        }))
        if (this.branches.length && this.selectedBranchId == null) {
          this.selectedBranchId = this.branches[0].id
        }
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
      } finally {
        this.loadingBranches = false
      }
    },
    async loadLayoutForBranch (branchId) {
      try {
        const normalizedBranchId = branchId != null ? Number(branchId) : null
        const params = {
          options: { page: 1, itemsPerPage: 1000 },
          search: null,
        }
        if (normalizedBranchId != null) {
          params.branch_id = normalizedBranchId
        }

        // halls
        const hallsRes = await this.$axios.get(this.$endpoint + 'restaurant/halls', { params })
        const hallsRaw = hallsRes.data?.data || hallsRes.data?.halls || []

        let halls = hallsRaw
        if (normalizedBranchId != null) {
          halls = halls.filter(h => (h.branch_id ?? h.branchId) === normalizedBranchId)
        }

        this.halls = halls.map(h => ({
          id: h.id,
          branchId: h.branch_id ?? h.branchId ?? null,
          name: h.name,
          width: h.width || 800,
          height: h.height || 600,
        }))

        this.activeHallId = this.halls.length ? this.halls[0].id : null
        hallIdCounter = this.halls.reduce((max, h) => Math.max(max, h.id), 0) + 1

        if (this.activeHallId) {
          await this.fetchTablesForHall(this.activeHallId)
        } else {
          this.tables = []
        }
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
      }
    },
    async saveLayout () {
      if (!this.tables.length) return
      this.saving = true
      try {
        const requests = this.tables
          .filter(t => t.id)
          .map(t => this.$axios.patch(
            this.$endpoint + `restaurant/tables/${t.id}/position`,
            { x: t.x, y: t.y },
          ))

        if (requests.length) {
          await Promise.all(requests)
        }
        this.showToast('چیدمان با موفقیت ذخیره شد')
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.showToast('خطا در ذخیره چیدمان', 'error')
      } finally {
        this.saving = false
      }
    },
    async selectHall (id) {
      this.activeHallId = id
      await this.fetchTablesForHall(id)
    },
    async fetchTablesForHall (hallId) {
      if (!this.$axios || !this.$endpoint) return
      this.loadingTables = true
      try {
        const params = {
          options: { page: 1, itemsPerPage: 1000 },
          search: null,
          hall_id: Number(hallId),
        }
        if (this.selectedBranchId != null) {
          params.branch_id = Number(this.selectedBranchId)
        }
        const res = await this.$axios.get(this.$endpoint + 'restaurant/tables', { params })
        const raw = res.data?.data || res.data?.tables || []
        this.tables = raw.map(t => ({
          id: t.id,
          hallId: t.hall_id ?? t.hallId ?? hallId,
          number: String(t.number),
          capacity: t.capacity || 0,
          shape: t.shape || 'rectangle',
          status: t.status || 'free',
          x: Number(t.pos_x ?? t.x ?? 0),
          y: Number(t.pos_y ?? t.y ?? 0),
        }))
        this.tableCountByHallId = { ...this.tableCountByHallId, [hallId]: this.tables.length }
        const maxId = this.tables.reduce((m, t) => Math.max(m, t.id), 0)
        if (maxId >= tableIdCounter) tableIdCounter = maxId + 1
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.tables = []
      } finally {
        this.loadingTables = false
      }
    },
    tablesCountForHall (hallId) {
      if (hallId === this.activeHallId) return this.tables.length
      return this.tableCountByHallId[hallId] ?? 0
    },
    async addTable () {
      if (!this.activeHallId) {
        alert('ابتدا یک سالن انتخاب یا ایجاد کنید.')
        return
      }
      if (!this.newTable.number) {
        alert('شماره میز را وارد کنید.')
        return
      }
      const baseX = 40 + this.tables.length * 80
      const baseY = 60

      const payload = {
        tables: [
          {
            branch_id: this.selectedBranchId,
            hall_id: this.activeHallId,
            number: this.newTable.number,
            capacity: this.newTable.capacity || 4,
            shape: this.newTable.shape,
            x: baseX,
            y: baseY,
          },
        ],
      }

      try {
        await this.$axios.post(
          this.$endpoint + 'restaurant/tables/bulk',
          payload,
        )
        await this.fetchTablesForHall(this.activeHallId)
        this.showToast('میز با موفقیت اضافه شد')
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.showToast('خطا در افزودن میز', 'error')
        this.tables.push({
          id: tableIdCounter++,
          hallId: this.activeHallId,
          number: this.newTable.number,
          capacity: this.newTable.capacity || 4,
          shape: this.newTable.shape,
          status: 'free',
          x: baseX,
          y: baseY,
        })
      }
      this.newTable.number = ''
    },
    selectTable (table, event) {
      this.selectedTableId = table.id
      this.dragging = true
      this.dragTableId = table.id
      const rect = this.$refs.canvas.getBoundingClientRect()
      this.dragOffset.x = event.clientX - rect.left - table.x
      this.dragOffset.y = event.clientY - rect.top - table.y
    },
    startDrag () {
      // فقط وقتی روی میز کلیک شده درگ می‌کنیم
    },
    stopDrag () {
      this.dragging = false
      this.dragTableId = null
    },
    onDrag (event) {
      if (!this.dragging || !this.dragTableId) return
      const canvas = this.$refs.canvas
      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left - this.dragOffset.x
      const y = event.clientY - rect.top - this.dragOffset.y
      const table = this.tables.find(t => t.id === this.dragTableId)
      if (!table) return
      table.x = Math.max(0, Math.min(x, rect.width - 60))
      table.y = Math.max(0, Math.min(y, rect.height - 60))
    },
    closeHallDialog () {
      this.hallDialog = false
      this.editingHallId = null
      this.newHall = {
        branchId: null,
        name: '',
        width: 800,
        height: 600,
      }
    },
    openHallDialog () {
      this.editingHallId = null
      this.newHall.branchId = this.selectedBranchId
      this.newHall.name = ''
      this.newHall.width = 800
      this.newHall.height = 600
      this.hallDialog = true
    },
    editHall (hall) {
      this.editingHallId = hall.id
      this.newHall = {
        branchId: hall.branchId ?? this.selectedBranchId,
        name: hall.name,
        width: hall.width || 800,
        height: hall.height || 600,
      }
      this.hallDialog = true
    },
    async submitHall () {
      if (!this.newHall.name) return
      if (this.branches.length && this.newHall.branchId == null) {
        alert('شعبه را انتخاب کنید.')
        return
      }
      const payload = {
        branch_id: this.newHall.branchId,
        name: this.newHall.name,
        width: this.newHall.width || 800,
        height: this.newHall.height || 600,
      }
      try {
        if (this.editingHallId) {
          const res = await this.$axios.put(
            this.$endpoint + `restaurant/halls/${this.editingHallId}`,
            payload,
          )
          const updated = res.data?.data ?? res.data ?? {}
          const hall = this.halls.find(h => h.id === this.editingHallId)
          if (hall) {
            hall.branchId = updated.branch_id ?? payload.branch_id
            hall.name = updated.name ?? payload.name
            hall.width = updated.width ?? payload.width
            hall.height = updated.height ?? payload.height
          }
        } else {
          const res = await this.$axios.post(
            this.$endpoint + 'restaurant/halls',
            payload,
          )
          const created = res.data?.data || res.data || {}
          const id = created.id ?? hallIdCounter++
          const hall = {
            id,
            branchId: created.branch_id ?? created.branchId ?? this.newHall.branchId,
            name: created.name ?? this.newHall.name,
            width: created.width ?? payload.width,
            height: created.height ?? payload.height,
          }
          this.halls.push(hall)
          this.activeHallId = id
          await this.fetchTablesForHall(id)
        }
        const wasEdit = !!this.editingHallId
        this.closeHallDialog()
        this.showToast(wasEdit ? 'سالن با موفقیت ویرایش شد' : 'سالن با موفقیت اضافه شد')
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.showToast('خطا در ذخیره سالن', 'error')
      }
    },
    openDeleteHallConfirm (hall) {
      this.deleteConfirmHall = hall
    },
    closeDeleteHallConfirm () {
      this.deleteConfirmHall = null
    },
    async doDeleteHall () {
      const hall = this.deleteConfirmHall
      if (!hall) return
      this.closeDeleteHallConfirm()
      if (!this.$axios || !this.$endpoint) {
        this.halls = this.halls.filter(h => h.id !== hall.id)
        if (this.activeHallId === hall.id) {
          this.activeHallId = this.halls.length ? this.halls[0].id : null
          if (this.activeHallId) this.fetchTablesForHall(this.activeHallId)
          else this.tables = []
        } else {
          this.tables = this.tables.filter(t => t.hallId !== hall.id)
        }
        return
      }
      try {
        await this.$axios.delete(this.$endpoint + `restaurant/halls/${hall.id}`)
        this.halls = this.halls.filter(h => h.id !== hall.id)
        this.tables = this.tables.filter(t => t.hallId !== hall.id)
        if (this.activeHallId === hall.id) {
          this.activeHallId = this.halls.length ? this.halls[0].id : null
          if (this.activeHallId) await this.fetchTablesForHall(this.activeHallId)
          else this.tables = []
        }
        this.showToast('سالن با موفقیت حذف شد')
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.showToast('خطا در حذف سالن', 'error')
      }
    },
    openDeleteTableConfirm (table) {
      this.deleteConfirmTable = table
      this.selectedTableId = null
    },
    closeDeleteTableConfirm () {
      this.deleteConfirmTable = null
    },
    async doDeleteTable () {
      const table = this.deleteConfirmTable
      if (!table) return
      this.closeDeleteTableConfirm()
      if (!this.$axios || !this.$endpoint) {
        this.tables = this.tables.filter(t => t.id !== table.id)
        this.updateTableCountForHall(this.activeHallId)
        return
      }
      try {
        await this.$axios.delete(this.$endpoint + `restaurant/tables/${table.id}`)
        this.tables = this.tables.filter(t => t.id !== table.id)
        this.updateTableCountForHall(this.activeHallId)
        this.showToast('میز با موفقیت حذف شد')
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.showToast('خطا در حذف میز', 'error')
        await this.fetchTablesForHall(this.activeHallId)
      }
    },
    updateTableCountForHall (hallId) {
      if (hallId == null) return
      const count = this.tables.filter(t => t.hallId === hallId).length
      this.tableCountByHallId = { ...this.tableCountByHallId, [hallId]: count }
    },
    openTableEdit (table) {
      this.editingTable = table
      this.editTableForm = {
        number: String(table.number),
        capacity: table.capacity || 4,
        shape: table.shape || 'rectangle',
      }
      this.tableDialog = true
    },
    closeTableEdit () {
      this.tableDialog = false
      this.editingTable = null
    },
    async submitTableEdit () {
      if (!this.editingTable || !this.editTableForm.number.trim()) return
      const payload = {
        number: this.editTableForm.number.trim(),
        capacity: this.editTableForm.capacity || 4,
        shape: this.editTableForm.shape,
      }
      try {
        if (this.$axios && this.$endpoint) {
          await this.$axios.put(
            this.$endpoint + `restaurant/tables/${this.editingTable.id}`,
            payload,
          )
        }
        Object.assign(this.editingTable, {
          number: payload.number,
          capacity: payload.capacity,
          shape: payload.shape,
        })
        this.closeTableEdit()
        this.showToast('میز با موفقیت ویرایش شد')
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err)
        this.showToast('خطا در ویرایش میز', 'error')
      }
    },
  },
}
</script>

<style scoped>
.restaurant-halls-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: #ffffff;
  border-radius: 5px;
  min-height: calc(100vh - 80px - 24px);
  height: 100%;
  box-sizing: border-box;
}

.restaurant-halls-page :deep(.ab-ui-btn) {
  border-radius: 8px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header__title h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.page-header__title p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #777;
}

.page-content {
  flex: 1 1 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(0, 2.5fr);
  gap: 16px;
}

.canvas-card {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #666;
  flex-wrap: wrap;
  gap: 8px;
}

.canvas-header__table-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.canvas-header__table-label {
  font-weight: 600;
  margin-left: 8px;
}

.canvas-header__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #f5f5f5;
  font-size: 12px;
  cursor: pointer;
}

.canvas-header__action-btn:hover {
  background: #eee;
}

.canvas-header__action-btn--danger:hover {
  background: #ffebee;
  color: #c62828;
  border-color: #ffcdd2;
}

.canvas {
  position: relative;
  flex: 1;
  height: 480px; /* ensure visible drag area */
  border-radius: 16px;
  background-image: radial-gradient(#e0e0e0 1px, transparent 1px);
  background-size: 16px 16px;
  background-color: #fafafa;
  overflow: hidden;
}

.canvas-footer {
  margin-top: 8px;
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #777;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.legend-item::before {
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
}

.legend-item--free::before {
  background-color: #4caf50;
}

.legend-item--busy::before {
  background-color: #f44336;
}

.legend-item--reserved::before {
  background-color: #ff9800;
}

.table {
  position: absolute;
  min-width: 56px;
  min-height: 40px;
  padding: 6px 10px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 10px;
  border: 2px solid #4caf50;
  background-color: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  cursor: move;
  user-select: none;
}

.table--circle {
  border-radius: 999px;
  width: 48px;
  height: 48px;
}

.table--square {
  width: 52px;
  height: 52px;
}

.table--rectangle {
  min-width: 72px;
  height: 44px;
}

.table--status-free {
  border-color: #4caf50;
  background-color: #e8f5e9;
  color: #2e7d32;
}

.table--status-busy {
  border-color: #f44336;
  background-color: #ffebee;
  color: #c62828;
}

.table--status-reserved {
  border-color: #ff9800;
  background-color: #fff3e0;
  color: #ef6c00;
}

.table--active {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.4);
}

.table__number {
  font-weight: 600;
}

.table__seats {
  font-size: 10px;
}

.sidebars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sidebars__card {
  padding: 14px;
}

.sidebars__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.branch-select-wrap {
  margin-bottom: 12px;
}

.branch-select-wrap .ab-ui-select {
  min-width: 0;
}

.halls-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hall-item {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #eee;
  background-color: #fafafa;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.hall-item--active {
  border-color: var(--v-theme-primary, #1976d2);
  background-color: #fafafa;
}

.hall-item__main {
  flex: 1;
  padding: 8px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: right;
  min-width: 0;
}

.hall-item__title span {
  font-size: 13px;
  font-weight: 500;
}

.hall-item__title small {
  display: block;
  font-size: 11px;
  color: #777;
}

.hall-item__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  flex-shrink: 0;
}

.hall-item__btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f0f0f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #555;
}

.hall-item__btn:hover {
  background: #e0e0e0;
}

.hall-item__btn--edit:hover {
  background: #e3f2fd;
  color: #1565c0;
}

.hall-item__btn--danger:hover {
  background: #ffebee;
  color: #c62828;
}

.add-table-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.shape-toggle {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shape-toggle__label {
  font-size: 12px;
  color: #555;
}

.shape-toggle__buttons {
  display: flex;
  gap: 6px;
}

.shape-pill {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background-color: #f7f7f7;
  padding: 4px 12px;
  font-size: 11px;
  cursor: pointer;
}

.shape-pill__icon {
  font-size: 18px;
  opacity: 0.85;
}

.shape-pill--active .shape-pill__icon {
  opacity: 1;
}

.shape-pill--active {
  border-color: currentColor;
  background-color: rgba(0, 0, 0, 0.02);
}

/* انیمیشن باز و بسته شدن دیالوگ */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}
.dialog-fade-enter-active .dialog,
.dialog-fade-enter-active .confirm-dialog,
.dialog-fade-leave-active .dialog,
.dialog-fade-leave-active .confirm-dialog {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.dialog-fade-enter,
.dialog-fade-leave-to {
  opacity: 0;
}
.dialog-fade-enter .dialog,
.dialog-fade-enter .confirm-dialog,
.dialog-fade-leave-to .dialog,
.dialog-fade-leave-to .confirm-dialog {
  transform: scale(0.96);
  opacity: 0;
}

.dialog-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  direction: rtl;
  background-color: #fff;
  border-radius: 16px;
  width: 480px;
  max-width: calc(100% - 32px);
  padding: 16px 18px 14px;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.2);
}

.dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
}

.dialog__close {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
}

.dialog__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dialog__row {
  display: flex;
  gap: 8px;
}

.dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.confirm-dialog {
  direction: rtl;
  background-color: #fff;
  border-radius: 16px;
  width: 400px;
  max-width: calc(100% - 32px);
  padding: 24px;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.2);
  text-align: center;
}

.confirm-dialog__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background-color: #ffebee;
  color: #c62828;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.confirm-dialog__title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.confirm-dialog__message {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #555;
}

.confirm-dialog__message strong {
  color: #333;
}

.confirm-dialog__warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 24px;
  padding: 10px 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #e65100;
  background-color: #fff3e0;
  border-radius: 8px;
  border: 1px solid #ffe0b2;
}

.confirm-dialog__warning .mdi {
  font-size: 18px;
  flex-shrink: 0;
}

.confirm-dialog__footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.confirm-dialog__footer .ab-ui-btn {
  min-width: 100px;
}

@media (max-width: 1024px) {
  .page-content {
    grid-template-columns: 1fr;
  }
}
</style>

